// @givmo/fundraisers-module — Duplicate a campaign email (Jonathan, 2026-11-01), from a row's menu in the series
// and from the email's own page: the server makes a new draft with the email's message, subject, preview text
// and audience and no time (any email, one already out included), and the copy opens in the designer. Where an
// organization has more than one campaign the person chooses the one it goes in, this one to start with. Its
// subject is the original's, unchanged: the series and its page say "Copy of" beside it until it is changed
// (`EmailRow.untouchedCopy`), never in what a recipient reads.

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, Select, toast } from '@givmo/design-system';
import { EMAILS_COPY as C } from './emails-copy';
import { decodeEmail, emailRefusal } from './emails-decode';
import { emailKeys } from './emails-queries';
import { duplicateEmail } from './emails-transport';
import { useFundraisersModule } from './fundraisers-context';
import { portfolioOptions } from './fundraisers-queries';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';

export function useDuplicateEmail(fundraiserId: string): {
  isPending: boolean;
  /** Duplicate this email: at once with only one campaign to put it in, else through the dialog. */
  start: (emailId: string) => void;
  /** The chooser, for the host to draw. */
  dialog: ReactNode;
} {
  const config = useFundraisersModule();
  const orgRef = config.orgRef ?? '';
  const client = useQueryClient();
  const [choosing, setChoosing] = useState<string | null>(null);
  const [target, setTarget] = useState(fundraiserId);
  const campaigns = useQuery({ ...portfolioOptions(orgRef), enabled: orgRef.length > 0 });
  const choices = (campaigns.data ?? []).map((campaign) => ({
    value: campaign.id,
    label:
      campaign.id === fundraiserId ? C.duplicateThisCampaign(campaign.name ?? '') : campaign.name,
  }));

  const mutation = useMutation({
    mutationFn: (copy: { emailId: string; target: string }) =>
      duplicateEmail(orgRef, fundraiserId, copy.emailId, copy.target),
    onSuccess: async (body, copied) => {
      const copy = decodeEmail(body);
      await Promise.all(
        [fundraiserId, copied.target].map((id) =>
          client.invalidateQueries({ queryKey: emailKeys.series(orgRef, id) }),
        ),
      );
      setChoosing(null);
      if (copy === null) {
        toast.error(C.duplicateFailed);
        return;
      }
      config.onNavigate(FUNDRAISERS_ROUTES.emailDesign(copied.target, copy.id));
    },
    onError: (error) => {
      const refusal = emailRefusal(error);
      toast.error(refusal.kind === 'refused' ? refusal.message : C.duplicateFailed);
    },
  });

  const start = (emailId: string) => {
    setTarget(fundraiserId);
    if (!campaigns.isPending && choices.length < 2) {
      mutation.mutate({ emailId, target: fundraiserId });
    } else setChoosing(emailId);
  };

  const dialog = (
    <Dialog.Root open={choosing !== null} onOpenChange={(open) => !open && setChoosing(null)}>
      <Dialog.Content
        className="fundraisers-dialog flex flex-col gap-16"
        testId="fundraiser-email-duplicate-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {C.duplicateTitle}
        </Dialog.Title>
        <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {C.duplicateBody}
        </Dialog.Description>
        <Select
          appearance="saas"
          aria-label={C.duplicateCampaign}
          value={target}
          options={choices}
          onValueChange={(value) => value !== null && setTarget(value)}
          testId="fundraiser-email-duplicate-target"
        />
        <div className="mt-8 flex justify-end gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={mutation.isPending}
            onClick={() => setChoosing(null)}
            testId="fundraiser-email-duplicate-cancel"
          >
            {C.duplicateCancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={mutation.isPending}
            onClick={() => choosing !== null && mutation.mutate({ emailId: choosing, target })}
            testId="fundraiser-email-duplicate-confirm"
          >
            {C.duplicate}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );

  return { isPending: mutation.isPending, start, dialog };
}
